import { useEffect, useState } from "react";
import { AiHint, PhotoThumb } from "@/components/scan/Scan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormError } from "@/components/common/ui-bits";
import { api, errMsg } from "@/lib/api";
import { AMOUNT_ERROR, centsToInput, formatDate, isValidAmount, money } from "@/lib/format";

export const BILL_STATUS = {
  Paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Partly paid": "bg-amber-100 text-amber-900 border-amber-300",
  Overdue: "bg-rose-100 text-rose-800 border-rose-300",
  Unpaid: "bg-sky-50 text-sky-800 border-sky-200",
};

export const BillStatus = ({ status, testId }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${BILL_STATUS[status]}`} data-testid={testId}>{status}</span>
);

export const period = (b) => `${formatDate(b.period_start)} – ${formatDate(b.period_end)}`;

export function BillSummaryRow({ b, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(b)} data-testid={`bill-row-${b.id}`}
      className="w-full text-left px-4 py-3.5 hover:bg-secondary/40 transition-colors grid grid-cols-2 md:grid-cols-[1.6fr_1.4fr_1fr_1fr_1fr_1fr_auto] gap-x-4 gap-y-1 items-center">
      <div className="col-span-2 md:col-span-1 min-w-0">
        <div className="font-semibold text-sm truncate">{b.vendor}{b.bill_number && <span className="text-muted-foreground font-normal"> · {b.bill_number}</span>}</div>
        <div className="text-[11px] text-muted-foreground truncate">{b.category_name}</div>
      </div>
      <div className="text-xs text-muted-foreground col-span-2 md:col-span-1">Period {period(b)}</div>
      <div className="text-xs"><span className="md:hidden text-muted-foreground">Due </span>{formatDate(b.due_date)}</div>
      <div className="text-xs font-num text-right md:text-left"><span className="md:hidden text-muted-foreground">Owed </span>{money(b.amount_cents)}</div>
      <div className="text-xs font-num"><span className="md:hidden text-muted-foreground">Paid </span>{money(b.paid_cents)}</div>
      <div className="text-sm font-num font-semibold text-right md:text-left" data-testid={`bill-balance-${b.id}`}><span className="md:hidden text-muted-foreground text-xs font-normal">Balance </span>{money(b.balance_cents)}</div>
      <div className="col-span-2 md:col-span-1 md:text-right"><BillStatus status={b.status} testId={`bill-status-${b.id}`} /></div>
    </button>
  );
}

const blank = { category_id: "", vendor: "", bill_number: "", account_number: "", amount: "", period_start: "", period_end: "", due_date: "", notes: "" };

export function BillFormDialog({ open, bill, prefill, ai, onClose, onSaved }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/bills/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(bill ? { ...blank, ...bill, amount: centsToInput(bill.amount_cents) } : prefill ? { ...blank, ...prefill } : blank);
  }, [open, bill, prefill]);
  // a scanned category that isn't a bill category (e.g. "Other") is dropped once the list is known
  useEffect(() => {
    if (cats.length && form.category_id && !cats.some((c) => c.id === form.category_id)) setForm((f) => ({ ...f, category_id: "" }));
  }, [cats, form.category_id]);
  const catKnown = cats.some((c) => c.id === form.category_id);
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_id) return setError("Choose Utilities or Vendor Purchases & Supplier Expenses");
    if (!isValidAmount(form.amount)) return setError(AMOUNT_ERROR);
    if (form.period_start && form.period_end && form.period_end < form.period_start) return setError("Bill period end must be on or after start");
    if (!form.due_date) return setError("Choose a due date");
    try {
      const payload = { ...form, amount: String(form.amount).replace(/[,$]/g, "") };
      const { data } = bill ? await api.put(`/bills/${bill.id}`, payload) : await api.post("/bills", payload);
      onSaved(data);
    } catch (err) { setError(errMsg(err)); }
  };

  const field = (k, label, props = {}) => (
    <div className={props.wide ? "col-span-2" : ""}>
      <Label htmlFor={`bill-${k}`} className="text-xs">{label}</Label>
      <Input id={`bill-${k}`} value={form[k] || ""} onChange={set(k)} className="mt-1 h-10 rounded-xl" data-testid={`bill-form-${k.replace(/_/g, "-")}`} {...props.input} />
      <AiHint show={ai?.has(k)} testId={`bill-form-ai-${k.replace(/_/g, "-")}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-card" data-testid="bill-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{bill ? "Edit bill" : "Add bill"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <div className="text-xs mb-1.5">Category</div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button type="button" key={c.id} onClick={() => { setForm((f) => ({ ...f, category_id: c.id })); setError(""); }}
                  data-testid={`bill-form-category-${c.key}`} aria-pressed={form.category_id === c.id}
                  className={`rounded-full border px-3.5 py-2 text-sm font-medium ${form.category_id === c.id ? "bg-primary border-primary" : "bg-card border-border"}`}>{c.name}</button>
              ))}
            </div>
            <AiHint show={ai?.has("category_id") && catKnown} testId="bill-form-ai-category" />
          </div>
          {field("vendor", "Vendor", { wide: true, input: { list: "bill-vendors" } })}
          {field("bill_number", "Bill / invoice # (optional)")}
          {field("account_number", "Account # (optional)")}
          {field("amount", "Amount owed (BSD)", { input: { inputMode: "decimal", placeholder: "0.00" } })}
          {field("due_date", "Due date", { input: { type: "date" } })}
          {field("period_start", "Bill period start", { input: { type: "date" } })}
          {field("period_end", "Bill period end", { input: { type: "date" } })}
          <div className="col-span-2"><Label htmlFor="bill-notes" className="text-xs">Notes (optional)</Label>
            <Textarea id="bill-notes" rows={2} value={form.notes} onChange={set("notes")} className="mt-1 rounded-xl" data-testid="bill-form-notes" /></div>
          <div className="col-span-2 space-y-3">
            <FormError message={error} testId="bill-form-error" />
            <Button type="submit" className="w-full h-11 rounded-xl" data-testid="bill-form-submit">{bill ? "Save bill" : "Add bill"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
